import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Loader2, BadgePercent, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../App';
import { ASSETS, WINBACK_PACKAGES } from '../constants/site';

export default function Offer() {
  const { pkgId } = useParams();
  const offer = WINBACK_PACKAGES[pkgId];
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);

  if (!offer) {
    return (
      <div className="min-h-screen bg-[#050B14] flex items-center justify-center px-6 text-center">
        <div>
          <p className="text-slate-400 mb-6">This offer link is no longer valid.</p>
          <Link to="/" className="text-amber-300 hover:underline">Back to GSLAB</Link>
        </div>
      </div>
    );
  }

  const claim = async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/payments/checkout', {
        package_id: pkgId,
        origin_url: window.location.origin,
        prospect_email: email,
        ref: sessionStorage.getItem('gslab_ref') || '',
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Checkout could not start. Please try again.');
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050B14] circuit-bg flex items-center justify-center px-6 py-20">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="gold-ring rounded-3xl bg-[#0F1C33]/80 p-10 max-w-lg w-full text-center"
        data-testid="offer-page"
      >
        <img src={ASSETS.icon} alt="GSLAB" className="h-16 w-16 rounded-xl mx-auto mb-6" />
        <div className="inline-flex items-center gap-2 border border-amber-500/30 bg-amber-500/10 rounded-full px-4 py-1.5 mb-6">
          <BadgePercent size={14} className="text-amber-400" />
          <span className="font-mono-tech text-[11px] tracking-[0.25em] text-amber-300 uppercase">Welcome-back offer · 15% off</span>
        </div>
        <h1 className="font-display font-bold text-2xl mb-2">{offer.name}</h1>
        <p className="text-sm text-slate-400 mb-6">Your discount is already applied — just finish checkout.</p>
        <div className="mb-8">
          <span className="text-slate-500 line-through text-xl mr-3">${offer.original.toLocaleString()}{offer.unit}</span>
          <span className="font-display font-extrabold text-4xl text-gradient-gold">${offer.price.toLocaleString()}</span>
          <span className="font-mono-tech text-xs text-slate-500 ml-2">{offer.unit}</span>
        </div>
        <input
          data-testid="offer-email-input"
          type="email"
          placeholder="Email for your receipt (optional)"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full bg-[#0B1528]/70 border border-slate-700/60 focus:border-amber-500/60 rounded-xl px-4 py-3 text-sm text-slate-200 outline-none transition-colors placeholder:text-slate-600 mb-4"
        />
        <button
          data-testid="offer-claim-button"
          onClick={claim}
          disabled={busy}
          className="w-full inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#D4AF37] via-[#F3C64F] to-[#D4AF37] text-[#050B14] font-bold rounded-full px-6 py-3.5 hover:shadow-[0_0_28px_rgba(212,175,55,0.4)] transition-shadow duration-300 disabled:opacity-60"
        >
          {busy ? <Loader2 size={17} className="animate-spin" /> : <ShieldCheck size={17} />}
          Claim 15% off — secure checkout
        </button>
        <p className="text-xs text-slate-500 mt-4">Offer valid for 7 days · Cards, Apple Pay & Google Pay accepted</p>
      </motion.div>
    </div>
  );
}
